import React, { useEffect, useState, useCallback, useMemo } from 'react';
import PropTypes from 'prop-types';
import { MDText } from 'i18n-react';
import NotificationsSettingsModal from './NotificationsSettingsModal';
import {
  useGetNotificationUserSettingsApi,
  useUpdateNotificationUserSettingsApi
} from '@adsk/bim360-matrix-react-api/es/hooks/notificationUserSettingsApi';
import { useProject } from '@adsk/bim360-matrix-react-api/es/contexts/project';
import { useNotification } from '@adsk/bim360-matrix-react-notification-manager/es/helpers/context';
import {
  NotificationLevels,
  NotificationTypes
} from '@adsk/bim360-matrix-react-notification-manager/es/helpers/consts';
import { useCurrentUser } from '@adsk/bim360-matrix-react-api/es/contexts/currentUser';
import { FREQUENCY_ORIGINS, DEFAULT_SETTING_TYPE, FREQUENCIES } from './helpers/consts';
import { useLocale } from '@adsk/bim360-matrix-react-helpers/es/locale';

const getSettingFromProject = (type, projectSettings) => ({
  value: projectSettings
    ? projectSettings[type] && projectSettings[type].frequency
      ? projectSettings[type].frequency
      : projectSettings[DEFAULT_SETTING_TYPE].frequency
    : null,
  origin: FREQUENCY_ORIGINS.PROJECT
});

const getSettingFromChange = (type, changes, projectSettings) => {
  if (typeof changes[type] !== 'undefined') {
    if (changes[type] === null) {
      return getSettingFromProject(type, projectSettings);
    } else {
      return {
        value: changes[type],
        origin: FREQUENCY_ORIGINS.CHANGES
      };
    }
  }
  return null;
};

const getSettingFromUser = (type, userSettings) => {
  const options = (userSettings && userSettings[type] && userSettings[type].options) || [];
  const userSettingsFrequency = userSettings && userSettings[type] && userSettings[type].frequency;

  return userSettingsFrequency && options.includes(userSettingsFrequency)
    ? {
      value: userSettingsFrequency,
      origin: FREQUENCY_ORIGINS.USER
    }
    : null;
};

const getOptionsFromProject = (type, projectSettings) =>
  projectSettings && projectSettings[type] ? projectSettings[type].options : null;

const getOptionsFromUser = (type, userSettings) =>
  userSettings && userSettings[type] ? userSettings[type].options : null;

const aggregateSettings = (userSettings, projectSettings, changes, projectServiceTypes) => {
  const allKeys = Object.keys({ ...userSettings, ...projectSettings });
  return allKeys
    .filter(key => key !== DEFAULT_SETTING_TYPE)
    .reduce((settings, type) => {
      const data =
        getSettingFromChange(type, changes, projectSettings) ||
        getSettingFromUser(type, userSettings) ||
        getSettingFromProject(type, projectSettings);
      const typeMeta = projectSettings ? projectSettings[type] : null;
      const entitlementKey = typeMeta ? typeMeta.entitlementKey : null;
      if (data.value && projectServiceTypes && projectServiceTypes.includes(entitlementKey)) {
        data.name = typeMeta ? typeMeta.label : null;
        settings[type] = data;
      }
      return settings;
    }, {});
};

const aggregateOptions = (userSettings, projectSettings) => {
  const allKeys = Object.keys({ ...userSettings, ...projectSettings });
  return allKeys
    .filter(key => key !== DEFAULT_SETTING_TYPE)
    .reduce((settings, type) => {
      settings[type] = getOptionsFromUser(type, userSettings) ||
        getOptionsFromProject(type, projectSettings) || [FREQUENCIES.INSTANTLY];
      return settings;
    }, {});
};

const NotificationsSettingsModalContainer = props => {
  const { T } = useLocale(({ locale }) => new MDText(require(`./i18n/${locale}.json`)));
  const { showNotification } = useNotification();
  const {
    isLoading: isLoadingSettings,
    error: settingsLoadError,
    data: settingsData,
    fetchSettings
  } = useGetNotificationUserSettingsApi();
  const {
    isLoading: isUpdating,
    data: updatedUserSettingsData,
    error: userSettingsUpdateError,
    doUpdate: updateUserSettings
  } = useUpdateNotificationUserSettingsApi();
  const [changes, setChanges] = useState({});

  const project = useProject();
  const currentUser = useCurrentUser();
  const isLoading = isLoadingSettings || !currentUser;
  const isError = !isLoading && !!settingsLoadError;
  const onExited = props.onExited;

  const onChange = useCallback(
    (id, value) => setChanges(changes => ({ ...changes, [id]: value })),
    []
  );

  useEffect(() => {
    if (props.show) {
      fetchSettings();
    }
  }, [props.show, fetchSettings]);

  useEffect(() => {
    if (!props.show) {
      setChanges({});
    }
  }, [props.show]);

  useEffect(() => {
    if (updatedUserSettingsData) {
      setChanges({});
      onExited();
      showNotification({
        type: NotificationTypes.TOAST,
        level: NotificationLevels.SUCCESS,
        message: T.translate('SAVE_SUCCESS_TOAST_MESSAGE')
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [updatedUserSettingsData, showNotification, T]);

  useEffect(() => {
    if (userSettingsUpdateError) {
      showNotification({
        type: NotificationTypes.SNACKBAR,
        level: NotificationLevels.ERROR,
        message: T.translate('ERROR_SAVING_MESSAGE')
      });
    }
  }, [showNotification, userSettingsUpdateError, T]);

  const onTryAgain = useCallback(() => {
    fetchSettings();
  }, [fetchSettings]);

  const userSettingsEmailFrequency =
    (settingsData && settingsData.userSettings && settingsData.userSettings.emailFrequency) || {};
  const userSettings = updatedUserSettingsData
    ? updatedUserSettingsData.emailFrequency
    : userSettingsEmailFrequency;

  const projectSettings =
    settingsData && settingsData.projectSettings
      ? settingsData.projectSettings.emailFrequency
      : null;
  const canEdit =
    settingsData && settingsData.projectSettings
      ? settingsData.projectSettings.allowUserSettings
      : false;

  const projectServiceTypes = useMemo(
    () => (project && project.services ? project.services.map(s => s.type) : null),
    [project]
  );

  const settings = useMemo(
    () => aggregateSettings(userSettings, projectSettings, changes, projectServiceTypes),
    [userSettings, projectSettings, changes, projectServiceTypes]
  );

  const options = useMemo(() => aggregateOptions(userSettings, projectSettings), [
    userSettings,
    projectSettings
  ]);

  const onUpdateChanges = useCallback(() => {
    const userEmailFrequency = Object.keys(userSettings)
      .filter(key => key !== DEFAULT_SETTING_TYPE)
      .reduce((obj, key) => {
        if (userSettings[key].frequency) {
          obj[key] = userSettings[key].frequency;
        }
        return obj;
      }, {});

    updateUserSettings({ data: { emailFrequency: { ...userEmailFrequency, ...changes }}});
  }, [updateUserSettings, changes, userSettings]);

  const onReset = useCallback(
    id => {
      if (userSettings && userSettings[id] && userSettings[id].frequency !== null) {
        setChanges(changes => ({ ...changes, [id]: null }));
      } else {
        setChanges(changes => {
          const newChanges = { ...changes };
          delete newChanges[id];
          return newChanges;
        });
      }
    },
    [userSettings]
  );

  const isDirty = useMemo(() => !!Object.keys(changes).length, [changes]);

  return (
    <NotificationsSettingsModal
      {...props}
      isLoading={isLoading}
      isError={isError}
      canEdit={canEdit}
      isSaving={isUpdating}
      settings={settings}
      options={options}
      onTryAgain={onTryAgain}
      onSave={onUpdateChanges}
      onChange={onChange}
      onReset={onReset}
      isDirty={isDirty}
      project={project}
      user={currentUser}
    />
  );
};

NotificationsSettingsModalContainer.propTypes = {
  /**
   * Controls whether or not to show the modal.
   */
  show: PropTypes.bool,
  /**
   * Callback to when closing the modal.
   */
  onExited: PropTypes.func
};

export default NotificationsSettingsModalContainer;
